// The offer priced per recipient (REC-1983). When the request names its
// recipients, the receptor gives each one an amount per unit and an optional
// surcharge, and the API takes `items` plus a `price` equal to their sum:
//
//   items: [{ recipientIndex, unitAmount, units, surcharge }]
//
// `units` is not the receptor's to choose: it is the company's number of
// legal representatives (1 when it has none) and 1 for a person, and the API
// refuses any other value. Amounts are whole pesos here, as the single price
// has always been, though the API takes two decimals.

import { formatCLP } from '@/lib/format.js';

// The amount as typed: digits, or thousands grouped with dots ("45.000").
// Anything else is refused rather than guessed. An empty surcharge is 0.
export function parsePesos(raw, { optional = false } = {}) {
  const value = String(raw ?? '').trim();
  if (!value) return optional ? 0 : null;
  if (!/^(?:\d+|\d{1,3}(?:\.\d{3})+)$/.test(value)) return null;
  const n = Number(value.replaceAll('.', ''));
  if (!Number.isSafeInteger(n)) return null;
  if (n === 0) return optional ? 0 : null;
  return n;
}

// One row per recipient, in the request's order, with the units the API
// expects and how the row reads.
export function breakdownRows(recipients) {
  if (!Array.isArray(recipients) || recipients.length === 0) return [];
  return recipients.map((r, recipientIndex) => {
    const company = r?.kind === 'EMPRESA';
    const reps = Number(r?.representatives);
    const units = company && reps > 0 ? reps : 1;
    const who = company
      ? reps > 0
        ? `Empresa con ${reps} ${reps === 1 ? 'representante' : 'representantes'}`
        : 'Empresa'
      : 'Persona natural';
    return { recipientIndex, units, who, perRepresentative: company };
  });
}

// The rows checked against what was typed. Returns the items for the API and
// their total, or the error per row and field when something does not parse.
export function breakdownItems(rows, values) {
  const errors = {};
  const items = [];
  let total = 0;
  for (const row of rows) {
    const typed = values?.[row.recipientIndex] ?? {};
    const unitAmount = parsePesos(typed.unitAmount);
    const surcharge = parsePesos(typed.surcharge, { optional: true });
    const rowErrors = {};
    if (unitAmount == null)
      rowErrors.unitAmount =
        'Indica un monto en pesos, entero y mayor que cero.';
    if (surcharge == null)
      rowErrors.surcharge =
        'Indica el recargo en pesos, entero, o déjalo vacío.';
    if (Object.keys(rowErrors).length) {
      errors[row.recipientIndex] = rowErrors;
      continue;
    }
    items.push({
      recipientIndex: row.recipientIndex,
      unitAmount,
      units: row.units,
      surcharge,
    });
    total += unitAmount * row.units + surcharge;
  }
  if (Object.keys(errors).length) return { errors, items: null, total: null };
  return { errors: {}, items, total };
}

// The row's subtotal while typing, or null until its amount parses.
export function rowSubtotal(row, typed) {
  const unitAmount = parsePesos(typed?.unitAmount);
  const surcharge = parsePesos(typed?.surcharge, { optional: true });
  if (unitAmount == null || surcharge == null) return null;
  return unitAmount * row.units + surcharge;
}

// The API's refusals of a breakdown. They mean the request the receptor sees
// no longer matches the task (recipients changed), so the answer is to load it
// again, not to fix a field.
export const BREAKDOWN_STALE_ERRORS = [
  'PriceBreakdownWithoutRecipients',
  'PriceBreakdownMismatch',
  'PriceBreakdownTotalMismatch',
];

// Pesos with the cents an amount entered from backoffice may carry: formatCLP
// rounds to whole pesos, and "2 × $30.000 = $60.001" would not add up.
const pesos = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 2,
});
const money = (n) =>
  Number.isInteger(n) ? formatCLP(n) : pesos.format(Math.round(n * 100) / 100);

// How one item adds up: "2 × $30.000 + $5.000 de recargo = $65.000", or just
// "$25.000" when there is nothing to add. In cents, so decimals add up.
export function amountText({ units, unitAmount, surcharge = 0 }) {
  const amount =
    (Math.round(unitAmount * 100) * units + Math.round(surcharge * 100)) / 100;
  const base =
    units > 1 ? `${units} × ${money(unitAmount)}` : money(unitAmount);
  const parts =
    surcharge > 0 ? `${base} + ${money(surcharge)} de recargo` : base;
  return parts === money(amount)
    ? money(amount)
    : `${parts} = ${money(amount)}`;
}

// The breakdown of an offer already made (`priceBreakdown` on the quote reads),
// one line per recipient. An offer without one, or made before REC-1983, reads
// as a single item with no recipient, and gives no lines: its price says it.
export function offerBreakdownLines(breakdown, recipients) {
  const items = Array.isArray(breakdown?.items) ? breakdown.items : [];
  if (!items.length || items.some((item) => item?.recipientIndex == null))
    return [];
  const rows = breakdownRows(recipients);
  return [...items]
    .sort((a, b) => a.recipientIndex - b.recipientIndex)
    .map((item) => {
      const who = rows[item.recipientIndex]?.who;
      const n = item.recipientIndex + 1;
      return {
        label: who ? `Destinatario ${n} · ${who}` : `Destinatario ${n}`,
        text: amountText({
          units: Number(item.units) || 1,
          unitAmount: Number(item.unitAmount) || 0,
          surcharge: Number(item.surcharge) || 0,
        }),
      };
    });
}

// The breakdown of an offer just sent, built from its items the way the API
// stores it, so the confirmation shows it before any re-read.
export function breakdownFromItems(items) {
  if (!Array.isArray(items) || !items.length) return null;
  const withAmount = items.map((item) => ({
    ...item,
    amount: item.unitAmount * item.units + (item.surcharge ?? 0),
  }));
  return {
    items: withAmount,
    total: withAmount.reduce((sum, item) => sum + item.amount, 0),
  };
}
